import React from 'react';
import { Box, Grid, Paper, Stack } from '@mui/material';
import Typography from '../i18n/LocalizedTypography';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { useTranslation } from 'react-i18next';
import { translateInline } from '../../i18n/inlineTranslations';

export interface PanchangAlmanacData {
  date: string;
  day?: string;
  tithi?: string;
  nakshatra?: string;
  yoga?: string;
  karana?: string;
  sunrise?: string;
  sunset?: string;
  moonrise?: string;
  moonset?: string;
  paksha?: string;
  rashi?: {
    moonSign?: string;
    sunSign?: string;
  };
  calendar?: {
    vikramSamvat?: number;
    shakaSamvat?: number;
    gujaratiSamvat?: number;
    vedicMonth?: {
      name?: string;
      displayName?: string;
      localized?: Record<string, string>;
      labelLocalized?: Record<string, string>;
      isAdhika?: boolean;
      system?: string;
    };
    ayana?: string;
    ritu?: string;
  };
  timings?: {
    auspicious?: Array<{ name: string; value: string }>;
    inauspicious?: Array<{ name: string; value: string }>;
  };
  details?: {
    tithiNumber?: number;
    nakshatraNumber?: number;
    nakshatraPada?: number;
    yogaNumber?: number;
    karanaNumber?: number;
  };
  panchangAnalysis?: {
    status?: 'supportive' | 'mixed' | 'caution';
    summary?: { en?: string; hi?: string };
    disclaimer?: { en?: string; hi?: string };
    labels?: Record<string, { sectionLords?: string; sectionReview?: string; planetaryRuler?: string; deity?: string; summary?: string; guidance?: string; importantNote?: string }>;
    limbs?: Array<{
      key: string;
      name: string;
      planetaryRuler?: string;
      alternatePlanetaryRuler?: string;
      deity?: string;
      nature?: string;
      guidance?: { en?: string; hi?: string };
      explanation?: { en?: string; hi?: string };
      nameLocalized?: Record<string, string>;
      planetaryRulerLocalized?: Record<string, string>;
      alternatePlanetaryRulerLocalized?: Record<string, string> | null;
      deityLocalized?: Record<string, string>;
    }>;
    cautions?: Array<{ key: string; name: string; explanation?: { en?: string; hi?: string } }>;
  };
}

interface PanchangAlmanacProps {
  panchang: PanchangAlmanacData;
  locationLabel?: string;
  compact?: boolean;
}

const theme = {
  page: 'var(--app-surface-neutral, #f7fbf8)',
  pageLight: 'var(--app-surface-green, #e8f4ed)',
  section: 'var(--app-surface-green, #dcefe5)',
  border: 'var(--app-border, #b8d8c5)',
  text: 'var(--app-ink-neutral, #1d3928)',
  deep: 'var(--app-ink-green, #237447)',
  highlight: 'var(--app-surface-green, #eff8f2)',
  accent: '#2e7d4f',
};

const HINDI_PANCHANG_COPY: Record<string, string> = {
  'Sunrise and Moonrise': 'सूर्योदय और चंद्रोदय', Sunrise: 'सूर्योदय', Sunset: 'सूर्यास्त', Moonrise: 'चंद्रोदय', Moonset: 'चंद्रास्त',
  Panchang: 'पंचांग', Tithi: 'तिथि', Nakshatra: 'नक्षत्र', Yoga: 'योग', Karana: 'करण', Weekday: 'वार', Paksha: 'पक्ष', Pada: 'पाद',
  'Lunar Month, Samvat and Rashi': 'चंद्र मास, संवत और राशि', 'Vedic Month': 'वैदिक मास', 'Vikram Samvat': 'विक्रम संवत', 'Shaka Samvat': 'शक संवत', 'Gujarati Samvat': 'गुजराती संवत', 'Moon Sign': 'चंद्र राशि', 'Sun Sign': 'सूर्य राशि', 'Nakshatra Pada': 'नक्षत्र पाद',
  'Ritu and Ayana': 'ऋतु और अयन', 'Vedic Ritu': 'वैदिक ऋतु', 'Vedic Ayana': 'वैदिक अयन', 'Sunrise Source': 'सूर्योदय स्रोत', 'Panchang Source': 'पंचांग स्रोत', 'Location based calculation': 'स्थान आधारित गणना', 'Kundlistar Ephemeris Engine': 'कुंडलिस्टार एपिमेरिस इंजन',
  'Auspicious Timings': 'शुभ समय', 'Inauspicious Timings': 'अशुभ समय', 'Brahma Muhurta': 'ब्रह्म मुहूर्त', 'Abhijit Muhurta': 'अभिजित मुहूर्त', 'Vijaya Muhurta': 'विजय मुहूर्त', 'Godhuli Muhurta': 'गोधूलि मुहूर्त', 'Rahu Kalam': 'राहु काल', Yamaganda: 'यमगण्ड', 'Gulika Kalam': 'गुलिक काल',
  'Krishna Paksha': 'कृष्ण पक्ष', 'Shukla Paksha': 'शुक्ल पक्ष', Budhavara: 'बुधवार', Varsha: 'वर्षा', Dakshinayana: 'दक्षिणायन', Uttarayana: 'उत्तरायण',
  'Panchang limbs are calculated for sunrise at the selected place. Advanced end times for each limb can be added when high-precision transition calculations are enabled.': 'पंचांग के अंग चुने गए स्थान के सूर्योदय पर गणना किए जाते हैं। उच्च-सटीकता संक्रमण गणना उपलब्ध होने पर प्रत्येक अंग का समाप्ति समय भी जोड़ा जा सकता है।',
};

const parseDate = (value: string) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return new Date();
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatFullDate = (value: string) => {
  return formatDisplayDate(parseDate(value));
};

const splitRows = (rows: Array<[string, React.ReactNode]>) => {
  const pairs: Array<Array<[string, React.ReactNode]>> = [];
  for (let i = 0; i < rows.length; i += 2) pairs.push(rows.slice(i, i + 2));
  return pairs;
};

const toTimingRows = (items: Array<{ name: string; value: string }>): Array<[string, React.ReactNode]> => (
  items.map((item) => [item.name, item.value])
);

function Section({ title, rows }: { title: string; rows: Array<[string, React.ReactNode]> }) {
  return (
    <Box sx={{ border: `1px solid ${theme.border}`, borderTop: 0 }}>
      <Typography sx={{ bgcolor: theme.section, color: theme.deep, px: 1.5, py: 0.8, fontWeight: 900, textAlign: 'left', fontSize: 14 }}>
        {title}
      </Typography>
      {splitRows(rows).map((pair, index) => (
        <Grid container key={`${title}-${index}`} sx={{ borderTop: `1px solid ${theme.border}` }}>
          {pair.map(([label, value]) => (
            <Grid size={{ xs: 12, sm: 6 }} key={label}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', minHeight: 38, borderRight: { sm: `1px solid ${theme.border}` }, bgcolor: "var(--app-surface-neutral, #fff)" }}>
                <Typography sx={{ px: 1.2, py: 0.9, fontWeight: 900, color: theme.deep, fontSize: 13 }}>
                  {label}
                </Typography>
                <Typography sx={{ px: 1.2, py: 0.9, color: theme.text, fontSize: 13, fontWeight: 700 }}>
                  {value || '—'}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
}

export default function PanchangAlmanac({ panchang, locationLabel, compact = false }: PanchangAlmanacProps) {
  const { i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || 'en').split(/[-_]/)[0];
  const tr = (value: string) => language === 'hi' ? HINDI_PANCHANG_COPY[value] || translateInline(language, value) : translateInline(language, value);
  const date = parseDate(panchang.date);
  const selectedDay = date.getDate();
  const auspicious = panchang.timings?.auspicious || [];
  const inauspicious = panchang.timings?.inauspicious || [];
  const analysis = panchang.panchangAnalysis;
  const localized = (value?: Record<string, string>) => value?.[language] || value?.en || '—';
  const labels = analysis?.labels?.[language] || analysis?.labels?.en || {};
  const vedicMonth = panchang.calendar?.vedicMonth;
  const vedicMonthName = vedicMonth?.localized?.[language] || vedicMonth?.localized?.en || vedicMonth?.displayName || vedicMonth?.name || '—';
  const vedicMonthLabel = vedicMonth?.labelLocalized?.[language] || vedicMonth?.labelLocalized?.en || 'Vedic Month';
  const rulerRows: Array<[string, React.ReactNode]> = (analysis?.limbs || []).map((limb) => [
    localized(limb.nameLocalized) || limb.name,
    `${labels.planetaryRuler || 'Planetary ruler'}: ${localized(limb.planetaryRulerLocalized) || limb.planetaryRuler || '—'}${limb.alternatePlanetaryRuler ? ` / ${localized(limb.alternatePlanetaryRulerLocalized || undefined)}` : ''} · ${labels.deity || 'Deity'}: ${localized(limb.deityLocalized) || limb.deity || '—'}`,
  ]);
  const reviewRows: Array<[string, React.ReactNode]> = [
    [labels.summary || 'Summary', localized(analysis?.summary as Record<string, string>)],
    ...((analysis?.cautions || []).map((item) => [localized((analysis?.limbs || []).find((limb) => limb.key === item.key)?.nameLocalized) || item.name, localized(item.explanation as Record<string, string>)] as [string, React.ReactNode])),
    ...((analysis?.limbs || []).filter((item) => item.guidance).map((item) => [`${localized(item.nameLocalized) || item.name} ${labels.guidance || 'Guidance'}`, localized(item.guidance as Record<string, string>)] as [string, React.ReactNode])),
    [labels.importantNote || 'Important note', localized(analysis?.disclaimer as Record<string, string>)],
  ];

  return (
    <Paper
      elevation={compact ? 0 : 2}
      sx={{
        overflow: 'hidden',
        borderRadius: 1,
        border: `1px solid ${theme.border}`,
        bgcolor: theme.page,
        color: theme.text,
        boxShadow: compact ? 'none' : '0 14px 34px rgba(35, 116, 71, 0.10)',
      }}
    >
      <Box sx={{ bgcolor: theme.accent, color: '#fff', px: 2, py: 1, fontWeight: 900 }}>
        {formatFullDate(panchang.date)}
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2, background: "linear-gradient(135deg, var(--app-surface-neutral, #edf8f1) 0%, var(--app-surface-green, #d8efe2) 52%, var(--app-surface-green, #c5e3d2) 100%)" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography variant={compact ? 'h6' : 'h5'} sx={{ fontWeight: 900, color: theme.deep }}>
              {panchang.details?.nakshatraNumber ? `${String(panchang.details.nakshatraNumber).padStart(2, '0')}, ` : ''}
              {panchang.nakshatra || 'Nakshatra'}
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {vedicMonthName} · {tr(panchang.paksha || 'Paksha')}, {tr(panchang.tithi || 'Tithi')}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              {locationLabel || 'Selected location'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography sx={{ textAlign: { xs: 'left', md: 'right' }, fontWeight: 900, textDecoration: 'underline' }}>
              {selectedDay.toString().padStart(2, '0')}
            </Typography>
            <Typography sx={{ textAlign: { xs: 'left', md: 'right' }, fontSize: 13, fontWeight: 800 }}>
              {formatFullDate(panchang.date)} · {tr(panchang.day || 'Vara')}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 1.25, py: 1.5, bgcolor: "var(--app-surface-neutral, #fff)", borderY: `1px solid ${theme.border}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ fontSize: 13, fontWeight: 800 }}>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{tr('Sunrise')}: {panchang.sunrise || '—'}</Typography>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{tr('Moonrise')}: {panchang.moonrise || '—'}</Typography>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{tr('Tithi')}: {panchang.tithi || '—'}</Typography>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{tr('Nakshatra')}: {panchang.nakshatra || '—'}</Typography>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{tr('Sunset')}: {panchang.sunset || '—'}</Typography>
        </Stack>
      </Box>

      <Section
        title={tr('Sunrise and Moonrise')}
        rows={[
          [tr('Sunrise'), panchang.sunrise],
          [tr('Sunset'), panchang.sunset],
          [tr('Moonrise'), panchang.moonrise],
          [tr('Moonset'), panchang.moonset],
        ]}
      />

      <Section
        title={tr('Panchang')}
        rows={[
          [tr('Tithi'), `${tr(panchang.tithi || '—')}${panchang.details?.tithiNumber ? ` (#${panchang.details.tithiNumber})` : ''}`],
          [tr('Nakshatra'), `${tr(panchang.nakshatra || '—')}${panchang.details?.nakshatraPada ? ` ${tr('Pada')} ${panchang.details.nakshatraPada}` : ''}`],
          [tr('Yoga'), `${tr(panchang.yoga || '—')}${panchang.details?.yogaNumber ? ` (#${panchang.details.yogaNumber})` : ''}`],
          [tr('Karana'), `${tr(panchang.karana || '—')}${panchang.details?.karanaNumber ? ` (#${panchang.details.karanaNumber})` : ''}`],
          [tr('Weekday'), tr(panchang.day || '—')],
          [tr('Paksha'), tr(panchang.paksha || '—')],
        ]}
      />

      {rulerRows.length > 0 && <Section title={labels.sectionLords || 'Panchang Lords and Deities'} rows={rulerRows} />}
      {analysis && <Section title={labels.sectionReview || 'Panchang Quality Review'} rows={reviewRows} />}

      <Section
        title={tr('Lunar Month, Samvat and Rashi')}
        rows={[
          [vedicMonthLabel, vedicMonthName],
          [tr('Vikram Samvat'), panchang.calendar?.vikramSamvat],
          [tr('Shaka Samvat'), panchang.calendar?.shakaSamvat],
          [tr('Gujarati Samvat'), panchang.calendar?.gujaratiSamvat],
          [tr('Moon Sign'), panchang.rashi?.moonSign],
          [tr('Sun Sign'), panchang.rashi?.sunSign],
          [tr('Nakshatra Pada'), panchang.details?.nakshatraPada],
        ]}
      />

      <Section
        title={tr('Ritu and Ayana')}
        rows={[
          [tr('Vedic Ritu'), tr(panchang.calendar?.ritu || '—')],
          [tr('Vedic Ayana'), tr(panchang.calendar?.ayana || '—')],
          [tr('Sunrise Source'), tr('Location based calculation')],
          [tr('Panchang Source'), tr('Kundlistar Ephemeris Engine')],
        ]}
      />

      <Section
        title={tr('Auspicious Timings')}
        rows={toTimingRows(auspicious.length ? auspicious.map((item) => ({ ...item, name: tr(item.name) })) : [{ name: tr('Abhijit Muhurta'), value: '—' }])}
      />

      <Section
        title={tr('Inauspicious Timings')}
        rows={toTimingRows(inauspicious.length ? inauspicious.map((item) => ({ ...item, name: tr(item.name) })) : [{ name: tr('Rahu Kalam'), value: '—' }])}
      />

      <Box sx={{ p: 1.25, bgcolor: theme.highlight, borderTop: `1px solid ${theme.border}` }}>
        <Typography sx={{ fontSize: 12, color: theme.deep, fontWeight: 800 }}>
          {tr('Panchang limbs are calculated for sunrise at the selected place. Advanced end times for each limb can be added when high-precision transition calculations are enabled.')}
        </Typography>
      </Box>
    </Paper>
  );
}
